const RECAPTCHA_V3_SCRIPT_ID = 'google-recaptcha-v3-script';

let recaptchaScriptPromise: Promise<void> | null = null;

const getGrecaptcha = () => (window as any).grecaptcha as
  | {
      ready?: (callback: () => void) => void;
      execute?: (siteKey: string, options: { action: string }) => Promise<string>;
    }
  | undefined;

export const getRecaptchaV3SiteKey = () =>
  String(import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY || '').trim();

const normalizeRecaptchaAction = (action: string) =>
  action.replace(/[^A-Za-z0-9_/-]/g, '_').slice(0, 100) || 'lead';

export const loadRecaptchaV3 = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  const siteKey = getRecaptchaV3SiteKey();
  if (!siteKey) {
    return Promise.reject(new Error('Google reCAPTCHA v3 site key is not configured.'));
  }

  const grecaptcha = getGrecaptcha();
  if (grecaptcha?.ready && grecaptcha?.execute) {
    return Promise.resolve();
  }

  if (recaptchaScriptPromise) {
    return recaptchaScriptPromise;
  }

  recaptchaScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(RECAPTCHA_V3_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Unable to load Google reCAPTCHA.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_V3_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Unable to load Google reCAPTCHA.'));

    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
};

export const executeRecaptchaV3 = async (action: string) => {
  const siteKey = getRecaptchaV3SiteKey();
  if (!siteKey) {
    throw new Error('Google reCAPTCHA v3 site key is not configured.');
  }

  const normalizedAction = normalizeRecaptchaAction(action);

  await loadRecaptchaV3();

  return new Promise<string>((resolve, reject) => {
    const grecaptcha = getGrecaptcha();
    if (!grecaptcha?.ready || !grecaptcha?.execute) {
      reject(new Error('Google reCAPTCHA is not ready.'));
      return;
    }

    grecaptcha.ready(() => {
      grecaptcha
        .execute(siteKey, { action: normalizedAction })
        .then(resolve)
        .catch(() => reject(new Error('Google reCAPTCHA verification could not start.')));
    });
  });
};

export const initializeRecaptchaV3Badge = async () => {
  const siteKey = getRecaptchaV3SiteKey();
  if (!siteKey) {
    throw new Error('Google reCAPTCHA v3 site key is not configured.');
  }

  await loadRecaptchaV3();

  return new Promise<void>((resolve, reject) => {
    const grecaptcha = getGrecaptcha();
    if (!grecaptcha?.ready || !grecaptcha?.execute) {
      reject(new Error('Google reCAPTCHA is not ready.'));
      return;
    }

    grecaptcha.ready(() => {
      grecaptcha
        .execute(siteKey, { action: normalizeRecaptchaAction('page_view') })
        .then(() => resolve())
        .catch(() => reject(new Error('Google reCAPTCHA badge could not be initialized.')));
    });
  });
};
